import { Select } from "@omnitok/ui";

import { useT } from "@/i18n";
import { findItem } from "@/lib/dashboard-helpers";
import {
  DEFAULT_ROW_DRILL_MODE,
  ROW_DRILL_MODES,
  type DisplayLabel,
  type RowDrillMode,
} from "@pervasivemind/dashboard-render";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import { Field } from "@/pages/builder/components/Field";
import { SynonymsEditor } from "@/pages/builder/components/SynonymsEditor";
import { CollapsibleSection } from "@/pages/builder/inspector/CollapsibleSection";
import { FieldGrid } from "@/pages/builder/inspector/FieldGrid";
import { ColumnWidthControl } from "@/pages/builder/inspector/ColumnWidthControl";
import { SqlPreview } from "@/pages/builder/inspector/SqlPreview";
import { StructuralRow } from "@/pages/builder/inspector/StructuralRow";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import type { DashboardItem, Synonyms } from "@/types";

interface ItemInspectorProps {
  draft: UseBuilderDraft;
  sectionId: string;
  itemId: string;
}

/**
 * Read-only inspector for the selected dashboard item.
 *
 * Iter 21: replaces the legacy ``ItemEditorWrapper`` + the 4 heavy
 * editors. Only label/title/description + synonyms stay editable
 * inline; everything else (SQL, type, format, variant, dimension,
 * metric, etc.) renders read-only.
 *
 * Iter 27: reorganises every variant into 3 ``CollapsibleSection``
 * blocks for density and discoverability:
 *
 *   1. **Identity** — bilingual labels + description + synonyms.
 *      Bilingual inputs sit side-by-side in a 2-col grid instead of
 *      stacked vertically. Expanded by default.
 *   2. **SQL Query** — the template SQL fragment(s) the engine uses
 *      to compute this element. ``SqlPreview`` shows ``KPI.sql`` /
 *      ``chart.metric`` / ``table.source_sql`` / etc. with a copy
 *      button. (Live preview that substitutes WHERE clauses lands in
 *      Phase 3b — backend endpoint pending; see the plan file.)
 *      Expanded by default.
 *   3. **Behavior** + **Structural** — advanced + debug fields.
 *      Collapsed by default to keep the inspector short for the
 *      common case.
 *
 * Structural edits flow through Claude via the report-builder skill.
 */
export function ItemInspector({
  draft,
  sectionId,
  itemId,
}: ItemInspectorProps): JSX.Element | null {
  const item = findItem(draft.dashboard, sectionId, itemId);
  if (!item) return null;

  // Patch helper — merges the diff into ``item.props`` via the
  // draft's updateItem path. Only used by the editable fields below.
  const patch = (next: Record<string, unknown>): void => {
    draft.updateItem(sectionId, itemId, {
      props: { ...(item.props ?? {}), ...next },
    });
  };

  // Cross-filter eligibility for table columns: a ``filter_key`` may only
  // target a plain dynamic-options global filter — select / autocomplete
  // (or type-less, which defaults to select), not locked, and without a
  // static ``options`` list (cell values come from data). Mirrors the
  // publish-time ``_validate_cross_filters`` guard in ``service.py``.
  const crossFilterableKeys = (draft.dashboard.global_filters ?? [])
    .filter((f) => {
      const type = f.type;
      const options = (f as { options?: unknown }).options;
      return (
        (type == null || type === "select" || type === "autocomplete") &&
        !f.locked &&
        !(Array.isArray(options) && options.length > 0)
      );
    })
    .map((f) => f.key);

  switch (item.element) {
    case "kpi":
      return <KpiInspector item={item} patch={patch} />;
    case "chart":
      return <ChartInspector item={item} patch={patch} />;
    case "table":
      return (
        <TableInspector
          item={item}
          patch={patch}
          crossFilterableKeys={crossFilterableKeys}
        />
      );
    case "text":
      return <TextInspector item={item} patch={patch} />;
    default:
      return null;
  }
}

// ── KPI ──────────────────────────────────────────────────────────────

interface InspectorVariantProps {
  item: DashboardItem;
  patch: (next: Record<string, unknown>) => void;
}

function KpiInspector({ item, patch }: InspectorVariantProps): JSX.Element {
  const { t } = useT();
  const props = (item.props ?? {}) as Record<string, unknown>;
  const label = props.label as DisplayLabel | undefined;
  const description = props.description as DisplayLabel | undefined;
  const synonyms = (props.synonyms as Synonyms | undefined) ?? undefined;

  const hasBehavior =
    (Array.isArray(props.ignore_filters) && props.ignore_filters.length > 0) ||
    asString(props.drill_target) !== "" ||
    asString(props.goal_direction) !== "" ||
    asString(props.sparkline_sql) !== "" ||
    asString(props.previous_sql) !== "" ||
    asString(props.breakdown_sql) !== "" ||
    asString(props.expected_sql) !== "";

  return (
    <div className="space-y-3">
      <IdentitySection
        labelKey="editor.kpi.labelLabel"
        descriptionKey="editor.kpi.descriptionLabel"
        labelValue={label ?? ""}
        descriptionValue={description ?? ""}
        synonyms={synonyms}
        patch={patch}
      />

      <CollapsibleSection
        title={t("inspector.sql.title")}
        iconSymbol="▣"
        defaultOpen
      >
        <SqlPreview
          label={t("inspector.kpi.sql")}
          sql={asString(props.sql)}
          onChange={(sql) => patch({ sql })}
          hint={
            t("inspector.kpi.sqlHint")
          }
          emptyText={t("inspector.sql.notSet") ?? "Not set"}
        />
        <div className="mt-3">
          <FieldGrid>
            <CompactField label={t("inspector.kpi.format")}>
              {asString(props.format) || "number"}
            </CompactField>
            <CompactField label={t("inspector.kpi.variant")}>
              {asString(props.variant) || "card"}
            </CompactField>
            {asString(props.unit) && (
              <CompactField label={t("inspector.kpi.unit")}>
                {asString(props.unit)}
              </CompactField>
            )}
            {asString(props.drill_target) && (
              <CompactField label={t("inspector.kpi.drillTarget")}>
                <code className="break-words text-xs">
                  {asString(props.drill_target)}
                </code>
              </CompactField>
            )}
          </FieldGrid>
        </div>
      </CollapsibleSection>

      {hasBehavior && (
        <CollapsibleSection
          title={t("inspector.behavior.title")}
          defaultOpen={false}
        >
          <div className="space-y-3">
            {Array.isArray(props.ignore_filters) &&
              props.ignore_filters.length > 0 && (
                <StructuralRow label={t("inspector.kpi.ignoreFilters")}>
                  <ChipList
                    items={(props.ignore_filters as unknown[]).map(String)}
                  />
                </StructuralRow>
              )}
            {asString(props.goal_direction) && (
              <StructuralRow
                label={t("inspector.kpi.goalDirection")}
                value={asString(props.goal_direction)}
              />
            )}
            {asString(props.previous_sql) && (
              <SqlPreview
                label={t("inspector.kpi.previousSql")}
                sql={asString(props.previous_sql)}
              />
            )}
            {asString(props.sparkline_sql) && (
              <SqlPreview
                label={t("inspector.kpi.sparklineSql")}
                sql={asString(props.sparkline_sql)}
              />
            )}
            {asString(props.expected_sql) && (
              <SqlPreview
                label={t("inspector.kpi.expectedSql")}
                sql={asString(props.expected_sql)}
              />
            )}
            {asString(props.breakdown_sql) && (
              <SqlPreview
                label={t("inspector.kpi.breakdownSql")}
                sql={asString(props.breakdown_sql)}
              />
            )}
          </div>
        </CollapsibleSection>
      )}

      <StructuralSection
        rows={[
          { label: "Type", value: "kpi" },
          { label: "ID", value: item.id || "—" },
        ]}
      />
    </div>
  );
}

// ── Chart ────────────────────────────────────────────────────────────

function ChartInspector({ item, patch }: InspectorVariantProps): JSX.Element {
  const { t } = useT();
  const props = (item.props ?? {}) as Record<string, unknown>;
  const title = props.title as DisplayLabel | undefined;
  const synonyms = (props.synonyms as Synonyms | undefined) ?? undefined;
  const chartType = asString(props.type) || "bar";
  const hasBehavior =
    Array.isArray(props.ignore_filters) && props.ignore_filters.length > 0;

  return (
    <div className="space-y-3">
      <CollapsibleSection
        title={t("inspector.identity.title")}
        defaultOpen
      >
        <Field label={t("editor.chart.titleLabel")}>
          <BilingualInput
            value={title ?? ""}
            onChange={(next) => patch({ title: next })}
          />
        </Field>
        <div className="mt-3">
          <Field
            label={t("editor.synonyms.title")}
            hint={t("editor.synonyms.hint")}
          >
            <SynonymsEditor
              value={synonyms}
              onChange={(next) => patch({ synonyms: next })}
            />
          </Field>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("inspector.sql.title")}
        iconSymbol="▣"
        defaultOpen
      >
        {asString(props.dimension) && (
          <div className="mb-3">
            <CompactField label={t("inspector.chart.dimension")}>
              <code className="break-words text-xs">
                {asString(props.dimension)}
              </code>
            </CompactField>
          </div>
        )}
        {asString(props.metric) && (
          <SqlPreview
            label={t("inspector.chart.metric")}
            sql={asString(props.metric)}
          />
        )}
        {asString(props.raw_sql) && (
          <div className="mt-3">
            <SqlPreview
              label={t("inspector.chart.rawSql")}
              sql={asString(props.raw_sql)}
              onChange={(raw_sql) => patch({ raw_sql })}
              hint={
                t("inspector.chart.rawSqlHint")
              }
            />
          </div>
        )}
        {Array.isArray(props.metrics) && props.metrics.length > 0 && (
          <div className="mt-3 space-y-2">
            <span className="text-xs font-semibold text-neutral-500">
              {t("inspector.chart.metrics")}
            </span>
            {(props.metrics as Array<{ label?: unknown; sql?: unknown }>).map(
              (m, i) => (
                <SqlPreview
                  key={i}
                  label={asString(m.label) || `#${i + 1}`}
                  sql={asString(m.sql)}
                />
              ),
            )}
          </div>
        )}
        <div className="mt-3">
          <FieldGrid>
            <CompactField label={t("inspector.chart.type")}>
              {chartType}
            </CompactField>
            {asString(props.sort) && (
              <CompactField label={t("inspector.chart.sort")}>
                {asString(props.sort)}
              </CompactField>
            )}
            {props.limit !== undefined && props.limit !== null && (
              <CompactField label={t("inspector.chart.limit")}>
                {String(props.limit)}
              </CompactField>
            )}
          </FieldGrid>
        </div>
        {(asString(props.x) || asString(props.y) || asString(props.size)) && (
          <div className="mt-3">
            <FieldGrid cols={3}>
              {asString(props.x) && (
                <CompactField label="x">
                  <code className="text-xs">{asString(props.x)}</code>
                </CompactField>
              )}
              {asString(props.y) && (
                <CompactField label="y">
                  <code className="text-xs">{asString(props.y)}</code>
                </CompactField>
              )}
              {asString(props.size) && (
                <CompactField label="size">
                  <code className="text-xs">{asString(props.size)}</code>
                </CompactField>
              )}
            </FieldGrid>
          </div>
        )}
      </CollapsibleSection>

      {hasBehavior && (
        <CollapsibleSection
          title={t("inspector.behavior.title")}
          defaultOpen={false}
        >
          {Array.isArray(props.ignore_filters) &&
            props.ignore_filters.length > 0 && (
              <StructuralRow label={t("inspector.chart.ignoreFilters")}>
                <ChipList
                  items={(props.ignore_filters as unknown[]).map(String)}
                />
              </StructuralRow>
            )}
        </CollapsibleSection>
      )}

      <StructuralSection
        rows={[
          { label: "Type", value: `chart · ${chartType}` },
          { label: "ID", value: item.id || "—" },
        ]}
      />
    </div>
  );
}

// ── Table ────────────────────────────────────────────────────────────

interface TableColumnDef {
  column?: unknown;
  label?: unknown;
  format?: unknown;
  synonyms?: Synonyms | null;
  render?: unknown;
  truncate?: unknown;
  max_width?: number | null;
  /** Cross-filter target — the global filter this column's cells set. */
  filter_key?: string;
}

interface TableInspectorProps extends InspectorVariantProps {
  /** Keys of the draft's global filters eligible as cross-filter
   * targets (select/autocomplete, not locked, no static options). */
  crossFilterableKeys: string[];
}

function TableInspector({
  item,
  patch,
  crossFilterableKeys,
}: TableInspectorProps): JSX.Element {
  const { t } = useT();
  const props = (item.props ?? {}) as Record<string, unknown>;
  const columns: TableColumnDef[] = Array.isArray(props.columns)
    ? (props.columns as unknown[]).filter(
        (c): c is TableColumnDef => typeof c === "object" && c !== null,
      )
    : [];

  const patchColumn = (idx: number, diff: Partial<TableColumnDef>): void => {
    const next = columns.map((c, i) => (i === idx ? { ...c, ...diff } : c));
    patch({ columns: next });
  };

  const hasBehavior =
    Array.isArray(props.ignore_filters) && props.ignore_filters.length > 0;

  return (
    <div className="space-y-3">
      <CollapsibleSection
        title={t("inspector.table.editable")}
        description={t("inspector.table.editableHint")}
        defaultOpen
      >
        <div className="space-y-2">
          {columns.map((col, idx) => (
            <div
              key={idx}
              className="rounded-md border border-neutral-200 bg-neutral-50 p-2"
            >
              <div className="mb-1.5 flex items-center gap-2 text-xs text-neutral-500">
                <span className="font-mono">
                  {asString(col.column) || `#${idx + 1}`}
                </span>
                {asString(col.format) && (
                  <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-neutral-600">
                    {asString(col.format)}
                  </span>
                )}
              </div>
              <BilingualInput
                value={(col.label as DisplayLabel | undefined) ?? ""}
                onChange={(next) => patchColumn(idx, { label: next })}
                placeholder={t("editor.table.labelPlaceholder")}
              />
              <div className="mt-2">
                <ColumnWidthControl
                  column={asString(col.column) || String(idx + 1)}
                  value={col.max_width}
                  onChange={(max_width) => patchColumn(idx, { max_width })}
                />
              </div>
              <div className="mt-2">
                <span className="block text-xs text-neutral-500">
                  {t("editor.synonyms.title")}
                </span>
                <SynonymsEditor
                  value={col.synonyms ?? undefined}
                  onChange={(next) => patchColumn(idx, { synonyms: next })}
                />
              </div>
              <div className="mt-2">
                <span className="block text-xs text-neutral-500">
                  {t("inspector.table.crossFilter")}
                </span>
                <Select
                  options={[
                    { value: "", label: t("inspector.table.crossFilterNone") },
                    // A stale or ineligible value stays visible so the
                    // admin can see it and clear it — publish validation
                    // rejects it anyway, and hiding it would make the
                    // rejection unexplainable from this screen.
                    ...(col.filter_key &&
                    !crossFilterableKeys.includes(col.filter_key)
                      ? [{ value: col.filter_key, label: col.filter_key }]
                      : []),
                    ...crossFilterableKeys.map((key) => ({
                      value: key,
                      label: key,
                    })),
                  ]}
                  value={col.filter_key ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    patchColumn(idx, {
                      filter_key: e.target.value || undefined,
                    })
                  }
                  size="sm"
                  fullWidth
                />
              </div>
            </div>
          ))}
          {columns.length === 0 && (
            <p className="text-xs text-neutral-500">
              {t("inspector.table.noColumns")}
            </p>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("inspector.sql.title")}
        iconSymbol="▣"
        defaultOpen
      >
        {asString(props.source_sql) && (
          <SqlPreview
            label={t("inspector.table.sourceSql")}
            sql={asString(props.source_sql)}
            onChange={(source_sql) => patch({ source_sql })}
            hint={
              t("inspector.table.sourceSqlHint")
            }
          />
        )}
        <div className="mt-3">
          <FieldGrid>
            <CompactField label={t("inspector.table.pageSize")}>
              {String(props.page_size ?? 50)}
            </CompactField>
            {asString(props.sort) && (
              <CompactField label={t("inspector.table.sort")}>
                {asString(props.sort)}
              </CompactField>
            )}
            {asString(props.row_drill_target) && (
              <CompactField
                label={t("inspector.table.rowDrill")}
              >
                <code className="break-words text-xs">
                  {asString(props.row_drill_target)}
                </code>
              </CompactField>
            )}
            {asString(props.row_drill_target) && (
              // `row_drill_mode` — the ONE editable field of the drill
              // wiring, chosen per report: swap (default) or accordion.
              // Only meaningful with a drill target. An absent value
              // shows the engine's documented default (swap — what the
              // report already does); a stale/unknown value stays visible
              // as an extra option so the admin can see it and fix it —
              // publish validation rejects it, and hiding it would make
              // that rejection unexplainable from this screen.
              <CompactField label={t("inspector.table.rowDrillMode")}>
                <Select
                  options={[
                    ...(asString(props.row_drill_mode) !== "" &&
                    !(ROW_DRILL_MODES as readonly string[]).includes(
                      asString(props.row_drill_mode),
                    )
                      ? [
                          {
                            value: asString(props.row_drill_mode),
                            label: `⚠ ${asString(props.row_drill_mode)}`,
                          },
                        ]
                      : []),
                    // Default first, so the selected option reads as such.
                    ...[
                      DEFAULT_ROW_DRILL_MODE,
                      ...ROW_DRILL_MODES.filter((m) => m !== DEFAULT_ROW_DRILL_MODE),
                    ].map((mode: RowDrillMode) => ({
                      value: mode,
                      label: t(`inspector.table.rowDrillMode.${mode}`),
                    })),
                  ]}
                  value={asString(props.row_drill_mode) || DEFAULT_ROW_DRILL_MODE}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    patch({ row_drill_mode: e.target.value })
                  }
                  size="sm"
                  fullWidth
                />
                <p className="mt-1 text-xs text-neutral-500">
                  {t("inspector.table.rowDrillModeHint")}
                </p>
              </CompactField>
            )}
          </FieldGrid>
        </div>
      </CollapsibleSection>

      {hasBehavior && (
        <CollapsibleSection
          title={t("inspector.behavior.title")}
          defaultOpen={false}
        >
          {Array.isArray(props.ignore_filters) &&
            props.ignore_filters.length > 0 && (
              <StructuralRow label={t("inspector.table.ignoreFilters")}>
                <ChipList
                  items={(props.ignore_filters as unknown[]).map(String)}
                />
              </StructuralRow>
            )}
        </CollapsibleSection>
      )}

      <StructuralSection
        rows={[
          { label: "Type", value: "table" },
          { label: "ID", value: item.id || "—" },
          { label: "Columns", value: String(columns.length) },
        ]}
      />
    </div>
  );
}

// ── Text ─────────────────────────────────────────────────────────────

function TextInspector({ item, patch }: InspectorVariantProps): JSX.Element {
  const { t } = useT();
  const props = (item.props ?? {}) as Record<string, unknown>;
  const content = asString(props.content);
  const sql = asString(props.sql);

  return (
    <div className="space-y-3">
      <CollapsibleSection
        title={t("inspector.identity.title")}
        defaultOpen
      >
        <Field
          label={t("inspector.text.contentLabel")}
          hint={t("inspector.text.contentHint")}
        >
          <textarea
            value={content}
            onChange={(e) => patch({ content: e.target.value })}
            className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[160px] font-mono text-xs"
            placeholder={t("inspector.text.contentPlaceholder")}
          />
        </Field>
      </CollapsibleSection>

      {sql && (
        <CollapsibleSection
          title={t("inspector.sql.title")}
          iconSymbol="▣"
          defaultOpen
        >
          <SqlPreview
            label={t("inspector.text.sql")}
            sql={sql}
            hint={
              t("inspector.text.sqlHint")
            }
          />
        </CollapsibleSection>
      )}

      <StructuralSection
        rows={[
          { label: "Type", value: "text" },
          { label: "ID", value: item.id || "—" },
        ]}
      />
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────────

interface IdentitySectionProps {
  labelKey: string;
  descriptionKey: string;
  labelValue: DisplayLabel | "";
  descriptionValue: DisplayLabel | "";
  synonyms?: Synonyms;
  patch: (next: Record<string, unknown>) => void;
}

/**
 * Shared "Identity" collapsible — bilingual label + description (in a
 * single column for now: BilingualInput already renders EN+ES
 * side-by-side internally) + synonyms.
 */
function IdentitySection({
  labelKey,
  descriptionKey,
  labelValue,
  descriptionValue,
  synonyms,
  patch,
}: IdentitySectionProps): JSX.Element {
  const { t } = useT();
  return (
    <CollapsibleSection
      title={t("inspector.identity.title")}
      defaultOpen
    >
      <div className="space-y-3">
        <Field label={t(labelKey)}>
          <BilingualInput
            value={labelValue ?? ""}
            onChange={(next) => patch({ label: next })}
          />
        </Field>
        <Field label={t(descriptionKey)}>
          <BilingualInput
            value={descriptionValue ?? ""}
            onChange={(next) => patch({ description: next })}
          />
        </Field>
        <Field
          label={t("editor.synonyms.title")}
          hint={t("editor.synonyms.hint")}
        >
          <SynonymsEditor
            value={synonyms}
            onChange={(next) => patch({ synonyms: next })}
          />
        </Field>
      </div>
    </CollapsibleSection>
  );
}

interface StructuralSectionProps {
  rows: ReadonlyArray<{ label: string; value: string }>;
}

function StructuralSection({ rows }: StructuralSectionProps): JSX.Element {
  const { t } = useT();
  return (
    <CollapsibleSection
      title={t("inspector.structural.title")}
      defaultOpen={false}
    >
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <StructuralRow key={i} label={r.label} value={r.value} mono />
        ))}
      </div>
    </CollapsibleSection>
  );
}

interface CompactFieldProps {
  label: string;
  children: React.ReactNode;
}

/**
 * Iter 27 — single label + value cell for use inside a ``FieldGrid``.
 * Compact alternative to the full ``Field`` wrapper for short
 * read-only values (format, variant, sort, limit, etc.).
 */
function CompactField({ label, children }: CompactFieldProps): JSX.Element {
  return (
    <div>
      <div className="text-xs font-semibold text-neutral-500">
        {label}
      </div>
      <div className="mt-0.5 text-[12px] text-neutral-800">{children}</div>
    </div>
  );
}

function ChipList({ items }: { items: ReadonlyArray<string> }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className="rounded-full bg-neutral-200 px-2 py-0.5 font-mono text-xs text-neutral-700"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}
